/**
 * FootballScene — Domain-specific Phaser scene for football training.
 *
 * Extends BaseGameScene with:
 *  - Striped grass, end zones with labels, yard lines, hash marks, yard numbers
 *  - Play clock HUD with countdown and warning pulse
 *  - Scrimmage line (dashed yellow) derived from QB position
 *  - Offense/defense color legend
 *  - Camera switching (top-down / QB perspective)
 *  - Event label display
 */

import Phaser from 'phaser';
import BaseGameScene from './BaseGameScene';
import EventBus from '../EventBus';
import RendererRegistry from '../RendererRegistry';

export default class FootballScene extends BaseGameScene {
  constructor() {
    super('FootballScene');
    this.clockText = null;
    this.clockArc = null;
    this.clockTimer = null;
    this.scrimmageGraphics = null;
    this.eventLabelText = null;
    this.hudElements = {};
    this.qbActorId = null;
    this._clockStarted = false;
    this._clockConfig = null;
    this.annotationGraphics = null;
    this.annotationTexts = [];
    this._pendingAnnotations = null;
    this._annotationRedrawTimer = null;
  }

  create() {
    super.create();
    this._tapActorMode = false;
    this._tapActorSelectableIds = [];
    this._tapActorCallback = null;

    EventBus.on('start-clock', (cfg) => {
      if (cfg) {
        this.startClock(cfg);
      } else {
        this.restartClock();
      }
    });
    EventBus.on('stop-clock', () => this.stopClock());
    EventBus.on('set-annotations', (annotations) => this.applyAnnotations(annotations));

    // tap_actor question mode — enable interactive actor selection on the field
    EventBus.on('enable-tap-actor', ({ selectableActorIds, onSelect }) => {
      this._enableTapActorMode(selectableActorIds, onSelect);
    });
    EventBus.on('disable-tap-actor', () => this._disableTapActorMode());

    // play-outcome animation — triggered after scoring an activity
    EventBus.on('play-outcome', (outcome) => this._showPlayOutcome(outcome));
  }

  applyManifest(manifest) {
    this.manifest = manifest;
    this.drawField();
    this.drawHUD();

    const defaultCam = (manifest.camera_options || [])
      .find(c => c.is_default)?.camera_id;
    if (defaultCam) {
      this.switchCamera(defaultCam);
    }
  }

  drawField() {
    const f = this.fieldRect();
    const layers = this.manifest?.background_layers || [];

    layers.forEach((layer) => {
      switch (layer.layer_type) {
        case 'striped_grass':
          this.drawStripedGrass(layer.config, f);
          break;
        case 'end_zones':
          this.drawEndZones(layer.config, f);
          break;
        case 'yard_lines':
          this.drawYardLines(layer.config, f);
          break;
        case 'hash_marks':
          this.drawHashMarks(layer.config, f);
          break;
        case 'yard_numbers':
          this.drawYardNumbers(layer.config, f);
          break;
        default:
          break;
      }
    });

    const border = this.add.graphics();
    border.lineStyle(2, 0xffffff, 0.5);
    border.strokeRect(f.x, f.y, f.w, f.h);
    border.setDepth(-1);
  }

  drawStripedGrass(config, f) {
    const colors = config?.colors || ['#2d7a2d', '#267026'];
    const stripeWidth = ((config?.stripe_width || 5) / 100) * f.w;
    const graphics = this.add.graphics();
    const numStripes = Math.ceil(f.w / stripeWidth);

    for (let i = 0; i < numStripes; i++) {
      const colorHex = parseInt(colors[i % colors.length].replace('#', ''), 16);
      graphics.fillStyle(colorHex, 1);
      graphics.fillRect(f.x + i * stripeWidth, f.y, stripeWidth, f.h);
    }
    graphics.setDepth(-5);
  }

  drawEndZones(config, f) {
    const homeColor = parseInt((config?.home_color || '#1a3a6e').replace('#', ''), 16);
    const awayColor = parseInt((config?.away_color || '#8b1a1a').replace('#', ''), 16);
    const ezWidth = (10 / 100) * f.w;

    const graphics = this.add.graphics();
    graphics.fillStyle(homeColor, 0.85);
    graphics.fillRect(f.x, f.y, ezWidth, f.h);
    graphics.fillStyle(awayColor, 0.85);
    graphics.fillRect(f.x + f.w - ezWidth, f.y, ezWidth, f.h);
    graphics.setDepth(-4);

    const homeLabel = config?.home_label || 'HOME';
    const awayLabel = config?.away_label || 'AWAY';

    const homeText = this.add.text(f.x + ezWidth / 2, f.y + f.h / 2, homeLabel, {
      fontSize: '20px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
    });
    homeText.setOrigin(0.5);
    homeText.setRotation(-Math.PI / 2);
    homeText.setAlpha(0.6);
    homeText.setDepth(-3);

    const awayText = this.add.text(f.x + f.w - ezWidth / 2, f.y + f.h / 2, awayLabel, {
      fontSize: '20px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
    });
    awayText.setOrigin(0.5);
    awayText.setRotation(Math.PI / 2);
    awayText.setAlpha(0.6);
    awayText.setDepth(-3);
  }

  drawYardLines(config, f) {
    const every = config?.every || 10;
    const color = parseInt((config?.color || '#ffffff').replace('#', ''), 16);
    const lineWidth = config?.line_width || 1;
    const graphics = this.add.graphics();
    graphics.lineStyle(lineWidth, color, 0.7);

    for (let yard = 0; yard <= 100; yard += every) {
      const x = f.x + (yard / 100) * f.w;
      graphics.lineBetween(x, f.y, x, f.y + f.h);
    }
    graphics.setDepth(-2);
  }

  drawHashMarks(config, f) {
    const color = parseInt((config?.color || 'rgba(255,255,255,0.4)').replace(/rgba?\(.*\)/, 'ffffff').replace('#', ''), 16);
    const positions = config?.positions || [0.33, 0.67];
    const graphics = this.add.graphics();
    graphics.lineStyle(1, color, 0.4);

    for (let yard = 10; yard <= 90; yard += 1) {
      const x = f.x + (yard / 100) * f.w;
      positions.forEach((pct) => {
        const y = f.y + pct * f.h;
        graphics.lineBetween(x - 3, y, x + 3, y);
      });
    }
    graphics.setDepth(-1);
  }

  drawYardNumbers(config, f) {
    const numbers = config?.numbers || [10, 20, 30, 40, 50, 40, 30, 20, 10];
    const yardPositions = config?.positions || [10, 20, 30, 40, 50, 60, 70, 80, 90];
    const fontSize = config?.font_size || 14;

    numbers.forEach((num, i) => {
      const x = f.x + (yardPositions[i] / 100) * f.w;

      const topText = this.add.text(x, f.y + f.h * 0.12, String(num), {
        fontSize: `${fontSize}px`, fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
      });
      topText.setOrigin(0.5);
      topText.setAlpha(0.5);
      topText.setDepth(-1);

      const botText = this.add.text(x, f.y + f.h * 0.88, String(num), {
        fontSize: `${fontSize}px`, fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
      });
      botText.setOrigin(0.5);
      botText.setAlpha(0.5);
      botText.setDepth(-1);
    });
  }

  drawHUD() {
    const hudElements = this.manifest?.hud_elements || [];

    hudElements.forEach((hud) => {
      switch (hud.element_type) {
        case 'legend':
          this.drawLegend(hud);
          break;
        case 'play_clock':
          this.drawClockPlaceholder(hud);
          break;
        case 'event_label':
          this.drawEventLabel(hud);
          break;
        default:
          break;
      }
    });
  }

  drawLegend(hud) {
    const items = hud.config?.items || [];
    const startX = 16;
    let startY = 16;

    items.forEach((item) => {
      const color = parseInt((item.color || '#ffffff').replace('#', ''), 16);
      const dot = this.add.circle(startX, startY, 6, color);
      dot.setDepth(50);
      const label = this.add.text(startX + 14, startY, item.label, {
        fontSize: '12px', fontFamily: 'Arial', color: '#ffffff',
      });
      label.setOrigin(0, 0.5);
      label.setDepth(50);
      startY += 20;
    });
  }

  drawClockPlaceholder(hud) {
    const { width } = this.scale;
    const x = width - 50;
    const y = 40;
    const radius = hud.config?.radius || 30;

    this._clockConfig = {
      duration_ms: hud.config?.duration_ms || 5000,
      warning_ms: hud.config?.warning_ms || 2000,
    };

    const bgCircle = this.add.circle(x, y, radius + 2, 0x000000, 0.5);
    bgCircle.setDepth(50);
    this.hudElements.clockBg = bgCircle;

    const initialDisplay = (this._clockConfig.duration_ms / 1000).toFixed(1);
    this.clockText = this.add.text(x, y, initialDisplay, {
      fontSize: '18px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
    });
    this.clockText.setOrigin(0.5);
    this.clockText.setDepth(51);

    const clockLabel = this.add.text(x, y + radius + 10, 'PLAY CLOCK', {
      fontSize: '10px', fontFamily: 'Arial', color: '#cccccc',
    });
    clockLabel.setOrigin(0.5);
    clockLabel.setDepth(50);
  }

  drawEventLabel() {
    const { width, height } = this.scale;
    this.eventLabelText = this.add.text(width / 2, height - 20, '', {
      fontSize: '16px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
      backgroundColor: 'rgba(0,0,0,0.5)', padding: { x: 12, y: 4 },
    });
    this.eventLabelText.setOrigin(0.5);
    this.eventLabelText.setDepth(50);
  }

  setActors(actors) {
    this.actors = actors;
    const qb = actors.find((a) => a.role === 'quarterback');
    if (qb) this.qbActorId = qb.actor_id;
    this.createActorSprites();

    if (this.currentCamera) {
      this.switchCamera(this.currentCamera);
    }

    this._redrawAnnotations();
  }

  updateFrame(frame) {
    super.updateFrame(frame);

    if (frame?.event_id && this.eventLabelText) {
      const displayName = frame.metadata?.display_name || frame.event_id;
      this.eventLabelText.setText(displayName);
    }

    if (frame?.actor_positions && this.qbActorId) {
      const qbState = frame.actor_positions[this.qbActorId];
      if (qbState?.position) {
        this.drawScrimmageLine(qbState.position.x);
      }
    }

    if (frame && this._clockConfig && !this._clockStarted) {
      this._clockStarted = true;
      this.startClock(this._clockConfig);
    }

    if (this._annotationRedrawTimer) {
      this._annotationRedrawTimer.remove(false);
    }
    const tweenMs = this.manifest?.animation_config?.tween_duration_ms || 400;
    this._annotationRedrawTimer = this.time.delayedCall(tweenMs + 60, () => {
      this._redrawAnnotations();
    });
  }

  restartClock() {
    this._clockStarted = false;
    if (this._clockConfig) {
      this._clockStarted = true;
      this.startClock(this._clockConfig);
    }
  }

  drawScrimmageLine(qbX) {
    if (this.scrimmageGraphics) this.scrimmageGraphics.destroy();

    const vp = this.manifest?.viewport || { width: 100 };
    const f = this.fieldRect();
    const screenX = f.x + (qbX / vp.width) * f.w;
    const lineX = screenX + (1 / vp.width) * f.w;

    this.scrimmageGraphics = this.add.graphics();
    this.scrimmageGraphics.lineStyle(2, 0xffeb3b, 0.7);

    const dashLen = 8;
    const gapLen = 6;
    for (let y = f.y; y < f.y + f.h; y += dashLen + gapLen) {
      this.scrimmageGraphics.lineBetween(lineX, y, lineX, Math.min(y + dashLen, f.y + f.h));
    }
    this.scrimmageGraphics.setDepth(5);
  }

  // ── Annotations ─────────────────────────────────────────────────

  _parseColor(raw, fallback) {
    if (!raw) return fallback;
    const s = String(raw).replace(/^0x/i, '').replace(/^#/, '');
    const parsed = parseInt(s, 16);
    return isNaN(parsed) ? fallback : parsed;
  }

  applyAnnotations(annotations) {
    this._pendingAnnotations = annotations;
    this._redrawAnnotations();
  }

  _redrawAnnotations() {
    this.clearAnnotations();
    const annotations = this._pendingAnnotations;
    if (!annotations || !Array.isArray(annotations) || annotations.length === 0) return;
    if (Object.keys(this.actorSprites).length === 0) return;

    this.annotationGraphics = this.add.graphics();
    this.annotationGraphics.setDepth(20);
    this.annotationTexts = [];

    annotations.forEach((ann) => {
      switch (ann.type) {
        case 'route':
          this.drawRoute(ann);
          break;
        case 'bracket':
          this.drawBracket(ann);
          break;
        case 'matchup':
          this.drawMatchup(ann);
          break;
        case 'label':
          this.drawAnnotationLabel(ann);
          break;
        case 'highlight':
          this.drawHighlight(ann);
          break;
        case 'zone':
          this.drawZone(ann);
          break;
        case 'spotlight':
          this.drawSpotlight(ann);
          break;
        default:
          break;
      }
    });
  }

  /**
   * Spotlight annotation: highlight specific actors, dim all others.
   * ann.actors: actor IDs to highlight (full opacity).
   * ann.dim_opacity: opacity for non-highlighted actors (0.0–1.0, default 0.3).
   */
  drawSpotlight(ann) {
    const highlighted = new Set(ann.actors || []);
    const dimAlpha = ann.dim_opacity !== undefined && ann.dim_opacity !== null
      ? ann.dim_opacity
      : 0.3;

    Object.entries(this.actorSprites || {}).forEach(([actorId, sprite]) => {
      if (sprite) {
        sprite.setAlpha(highlighted.has(actorId) ? 1.0 : dimAlpha);
      }
    });

    // Draw a glow ring around highlighted actors
    const graphics = this.annotationGraphics;
    highlighted.forEach((actorId) => {
      const sprite = this.actorSprites[actorId];
      if (!sprite) return;
      graphics.lineStyle(3, 0xffd700, 0.9);
      graphics.strokeCircle(sprite.x, sprite.y, 22);
    });

    if (ann.label && ann.actors?.length > 0) {
      const firstSprite = this.actorSprites[ann.actors[0]];
      if (firstSprite) {
        const text = this.add.text(firstSprite.x, firstSprite.y - 30, ann.label, {
          fontSize: '11px', fontFamily: 'Arial', color: '#ffd700', fontStyle: 'bold',
          backgroundColor: 'rgba(0,0,0,0.7)', padding: { x: 4, y: 2 },
        });
        text.setOrigin(0.5);
        text.setDepth(22);
        this.annotationTexts.push(text);
      }
    }
  }

  clearAnnotations() {
    // Restore all actor opacities when annotations are cleared
    Object.values(this.actorSprites || {}).forEach((sprite) => {
      if (sprite) sprite.setAlpha(1.0);
    });
    if (this.annotationGraphics) {
      this.annotationGraphics.destroy();
      this.annotationGraphics = null;
    }
    if (this.annotationTexts) {
      this.annotationTexts.forEach((t) => t.destroy());
      this.annotationTexts = [];
    }
  }

  drawRoute(ann) {
    const actors = ann.actors || [];
    if (actors.length < 1) return;
    const sprite = this.actorSprites[actors[0]];
    if (!sprite) return;

    const target = ann.position || {};
    if (!target.x && !target.y) return;
    const endPos = this.worldToScreen(target.x, target.y);

    const color = this._parseColor(ann.style?.color, 0x00bfff);
    const lineWidth = ann.style?.width || 2.5;

    this.annotationGraphics.lineStyle(lineWidth, color, 0.9);
    const bulge = Math.min(40, Math.max(15,
      Math.sqrt(Math.pow(endPos.x - sprite.x, 2) + Math.pow(endPos.y - sprite.y, 2)) * 0.15
    ));
    const ctrlX = (sprite.x + endPos.x) / 2;
    const ctrlY = Math.min(sprite.y, endPos.y) - bulge;
    const curve = new Phaser.Curves.QuadraticBezier(
      new Phaser.Math.Vector2(sprite.x, sprite.y),
      new Phaser.Math.Vector2(ctrlX, ctrlY),
      new Phaser.Math.Vector2(endPos.x, endPos.y)
    );
    curve.draw(this.annotationGraphics, 32);

    const angle = Math.atan2(endPos.y - ctrlY, endPos.x - ctrlX);
    const arrowLen = 10;
    this.annotationGraphics.fillStyle(color, 1);
    this.annotationGraphics.fillTriangle(
      endPos.x, endPos.y,
      endPos.x - arrowLen * Math.cos(angle - 0.5), endPos.y - arrowLen * Math.sin(angle - 0.5),
      endPos.x - arrowLen * Math.cos(angle + 0.5), endPos.y - arrowLen * Math.sin(angle + 0.5)
    );

    if (ann.label) {
      const text = this.add.text(endPos.x, endPos.y - 14, ann.label, {
        fontSize: '10px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
        backgroundColor: 'rgba(0,0,0,0.65)', padding: { x: 4, y: 2 },
      });
      text.setOrigin(0.5);
      text.setDepth(21);
      this.annotationTexts.push(text);
    }
  }

  drawBracket(ann) {
    const actors = ann.actors || [];
    if (actors.length < 2) return;
    const sprite1 = this.actorSprites[actors[0]];
    const sprite2 = this.actorSprites[actors[1]];
    const targetSprite = ann.target ? this.actorSprites[ann.target] : null;
    if (!sprite1 || !sprite2) return;

    const color = this._parseColor(ann.style?.color, 0xff6b6b);
    this.annotationGraphics.lineStyle(2, color, 0.7);

    if (targetSprite) {
      this.annotationGraphics.lineBetween(sprite1.x, sprite1.y, targetSprite.x, targetSprite.y);
      this.annotationGraphics.lineBetween(sprite2.x, sprite2.y, targetSprite.x, targetSprite.y);
    } else {
      const midX = (sprite1.x + sprite2.x) / 2;
      const midY = Math.min(sprite1.y, sprite2.y) - 20;
      const curve1 = new Phaser.Curves.QuadraticBezier(
        new Phaser.Math.Vector2(sprite1.x, sprite1.y),
        new Phaser.Math.Vector2(midX, midY),
        new Phaser.Math.Vector2(sprite2.x, sprite2.y)
      );
      curve1.draw(this.annotationGraphics, 24);
    }

    if (ann.label) {
      const lx = targetSprite ? targetSprite.x : (sprite1.x + sprite2.x) / 2;
      const ly = targetSprite ? targetSprite.y - 20 : Math.min(sprite1.y, sprite2.y) - 30;
      const lColor = `#${color.toString(16).padStart(6, '0')}`;
      const text = this.add.text(lx, ly, ann.label, {
        fontSize: '11px', fontFamily: 'Arial', color: lColor,
        backgroundColor: 'rgba(0,0,0,0.6)', padding: { x: 4, y: 2 },
      });
      text.setOrigin(0.5);
      text.setDepth(21);
      this.annotationTexts.push(text);
    }
  }

  drawMatchup(ann) {
    const actorSprite = ann.actors?.[0] ? this.actorSprites[ann.actors[0]] : null;
    const defenderSprite = ann.target ? this.actorSprites[ann.target] : null;
    if (!actorSprite || !defenderSprite) return;

    const color = this._parseColor(ann.style?.color, 0xfbbf24);
    this.annotationGraphics.lineStyle(2, color, 0.7);
    const dashLen = 6;
    const gapLen = 4;
    const dx = defenderSprite.x - actorSprite.x;
    const dy = defenderSprite.y - actorSprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return;
    const ux = dx / dist;
    const uy = dy / dist;
    let drawn = 0;
    while (drawn < dist) {
      const sx = actorSprite.x + ux * drawn;
      const sy = actorSprite.y + uy * drawn;
      const ex = Math.min(drawn + dashLen, dist);
      this.annotationGraphics.lineBetween(sx, sy, actorSprite.x + ux * ex, actorSprite.y + uy * ex);
      drawn += dashLen + gapLen;
    }

    if (ann.label) {
      const mx = (actorSprite.x + defenderSprite.x) / 2;
      const my = (actorSprite.y + defenderSprite.y) / 2 - 12;
      const lColor = `#${color.toString(16).padStart(6, '0')}`;
      const text = this.add.text(mx, my, ann.label, {
        fontSize: '10px', fontFamily: 'Arial', color: lColor,
        backgroundColor: 'rgba(0,0,0,0.6)', padding: { x: 3, y: 1 },
      });
      text.setOrigin(0.5);
      text.setDepth(21);
      this.annotationTexts.push(text);
    }
  }

  drawAnnotationLabel(ann) {
    const pos = ann.position || {};
    const screenPos = this.worldToScreen(pos.x || 50, pos.y || 26);
    const text = this.add.text(screenPos.x, screenPos.y, ann.text || ann.label || '', {
      fontSize: '13px', fontFamily: 'Arial', color: '#ffffff', fontStyle: 'bold',
      backgroundColor: 'rgba(0,0,0,0.7)', padding: { x: 8, y: 4 },
    });
    text.setOrigin(0.5);
    text.setDepth(21);
    this.annotationTexts.push(text);
  }

  drawHighlight(ann) {
    const actors = ann.actors || [];
    const color = this._parseColor(ann.style?.color, 0x3b82f6);
    actors.forEach((actorId) => {
      const sprite = this.actorSprites[actorId];
      if (!sprite) return;
      const glow = this.add.circle(sprite.x, sprite.y, 20, color, 0.3);
      glow.setDepth(9);
      this.tweens.add({
        targets: glow,
        alpha: { from: 0.3, to: 0.1 },
        scale: { from: 1, to: 1.4 },
        duration: 800,
        yoyo: true,
        repeat: -1,
      });
      this.annotationTexts.push(glow);
    });
  }

  drawZone(ann) {
    const pos = ann.position || {};
    const screenPos = this.worldToScreen(pos.x || 50, pos.y || 26);
    const f = this.fieldRect();
    const w = (ann.style?.width || 20) / 100 * f.w;
    const h = (ann.style?.height || 15) / 100 * f.h;
    const color = this._parseColor(ann.style?.color, 0x3b82f6);
    const alpha = ann.style?.alpha || 0.15;
    this.annotationGraphics.fillStyle(color, alpha);
    this.annotationGraphics.fillRect(screenPos.x - w / 2, screenPos.y - h / 2, w, h);
    this.annotationGraphics.lineStyle(1, color, 0.4);
    this.annotationGraphics.strokeRect(screenPos.x - w / 2, screenPos.y - h / 2, w, h);
  }

  // ── Play Clock ─────────────────────────────────────────────────────

  startClock(config) {
    this.stopClock();
    const durationMs = config?.duration_ms || 5000;
    const warningMs = config?.warning_ms || 2000;
    let remaining = durationMs;

    this.clockTimer = this.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => {
        remaining -= 100;
        if (remaining <= 0) {
          this.stopClock();
          EventBus.emit('clock-expired');
          if (this.clockText) this.clockText.setText('0.0');
          return;
        }

        const secs = (remaining / 1000).toFixed(1);
        if (this.clockText) {
          this.clockText.setText(secs);
          if (remaining <= warningMs) {
            this.clockText.setColor('#ff4444');
            if (this.hudElements.clockBg) {
              this.hudElements.clockBg.setFillStyle(0x440000, 0.6);
            }
          } else {
            this.clockText.setColor('#ffffff');
            if (this.hudElements.clockBg) {
              this.hudElements.clockBg.setFillStyle(0x000000, 0.5);
            }
          }
        }
      },
    });
  }

  stopClock() {
    if (this.clockTimer) {
      this.clockTimer.remove(false);
      this.clockTimer = null;
    }
  }

  // ── Camera switching ───────────────────────────────────────────────

  switchCamera(cameraId) {
    super.switchCamera(cameraId);
    const cam = this.cameras.main;
    const { width, height } = this.scale;

    if (cameraId === 'qb_perspective') {
      const qbSprite = this.actorSprites[this.qbActorId];
      if (qbSprite) {
        cam.setBounds(0, 0, width, height);
        cam.setZoom(1.0);
        cam.stopFollow();
        cam.setScroll(0, 0);
        cam.setFollowOffset(0, 0);

        this._clearQbIndicator();
        const cone = this.add.graphics();
        cone.setDepth(8);
        const coneLen = width * 0.4;
        const coneHalf = height * 0.35;
        cone.fillStyle(0x00bfff, 0.06);
        cone.beginPath();
        cone.moveTo(qbSprite.x, qbSprite.y);
        cone.lineTo(qbSprite.x + coneLen, qbSprite.y - coneHalf);
        cone.lineTo(qbSprite.x + coneLen, qbSprite.y + coneHalf);
        cone.closePath();
        cone.fillPath();
        cone.lineStyle(1, 0x00bfff, 0.25);
        cone.beginPath();
        cone.moveTo(qbSprite.x, qbSprite.y);
        cone.lineTo(qbSprite.x + coneLen, qbSprite.y - coneHalf);
        cone.moveTo(qbSprite.x, qbSprite.y);
        cone.lineTo(qbSprite.x + coneLen, qbSprite.y + coneHalf);
        cone.strokePath();
        this._qbIndicator = cone;
      }
    } else {
      cam.stopFollow();
      cam.setZoom(1.0);
      cam.setScroll(0, 0);
      cam.setFollowOffset(0, 0);
      this._clearQbIndicator();
    }
  }

  _clearQbIndicator() {
    if (this._qbIndicator) {
      this._qbIndicator.destroy();
      this._qbIndicator = null;
    }
  }

  // ── Tap-actor question mode ─────────────────────────────────────────

  /**
   * Enable tap-actor mode: make selectable actors interactive tap targets.
   * selectableActorIds: actor IDs that the learner can tap.
   * onSelect: callback(actorId) called when the learner taps an actor.
   */
  _enableTapActorMode(selectableActorIds, onSelect) {
    this._disableTapActorMode();
    this._tapActorMode = true;
    this._tapActorSelectableIds = selectableActorIds || [];
    this._tapActorCallback = onSelect;

    (selectableActorIds || []).forEach((actorId) => {
      const sprite = this.actorSprites[actorId];
      if (!sprite) return;

      sprite.setInteractive({ useHandCursor: true });

      // Highlight border to show tappability
      const gfx = this.add.graphics();
      gfx.lineStyle(3, 0x00ff88, 0.9);
      gfx.strokeCircle(sprite.x, sprite.y, 20);
      gfx.setDepth(25);
      sprite._tapHighlight = gfx;

      sprite.once('pointerdown', () => {
        if (this._tapActorMode && this._tapActorCallback) {
          this._tapActorCallback(actorId);
        }
      });
    });
  }

  _disableTapActorMode() {
    if (!this._tapActorMode) return;
    this._tapActorMode = false;

    this._tapActorSelectableIds.forEach((actorId) => {
      const sprite = this.actorSprites[actorId];
      if (!sprite) return;
      sprite.disableInteractive();
      if (sprite._tapHighlight) {
        sprite._tapHighlight.destroy();
        sprite._tapHighlight = null;
      }
    });

    this._tapActorSelectableIds = [];
    this._tapActorCallback = null;
  }

  // ── Play outcome animation ──────────────────────────────────────────

  /**
   * Display a brief outcome overlay after an activity is scored.
   * outcome: { animation_key, narrative_text, xp_awarded, payload }
   */
  _showPlayOutcome(outcome) {
    const { width, height } = this.scale;
    const key = outcome?.animation_key || 'correct';

    const colorMap = {
      big_gain: 0x00cc66,
      first_down: 0x1a73e8,
      short_gain: 0xfbbf24,
      sack: 0xe53935,
      correct: 0x00cc66,
      partial: 0xfbbf24,
      incorrect: 0xe53935,
    };
    const labelMap = {
      big_gain: 'BIG GAIN!',
      first_down: 'FIRST DOWN!',
      short_gain: 'Short Gain',
      sack: 'SACK!',
      correct: '✓ Correct',
      partial: '~ Correct',
      incorrect: '✗ Incorrect',
    };

    const color = colorMap[key] ?? 0x888888;
    const label = labelMap[key] ?? key.toUpperCase();

    // Background flash
    const flash = this.add.rectangle(width / 2, height / 2, width, height, color, 0.18);
    flash.setDepth(30);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 600,
      onComplete: () => flash.destroy(),
    });

    // Outcome text
    const text = this.add.text(width / 2, height * 0.38, label, {
      fontSize: '36px',
      fontFamily: 'Arial Black, Arial',
      color: `#${color.toString(16).padStart(6, '0')}`,
      stroke: '#000000',
      strokeThickness: 5,
    });
    text.setOrigin(0.5);
    text.setDepth(31);
    this.tweens.add({
      targets: text,
      y: height * 0.32,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });

    // XP badge
    if (outcome?.xp_awarded > 0) {
      const xpText = this.add.text(width / 2, height * 0.46, `+${outcome.xp_awarded} XP`, {
        fontSize: '20px', fontFamily: 'Arial', color: '#ffd700', fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 3,
      });
      xpText.setOrigin(0.5);
      xpText.setDepth(31);
      this.tweens.add({
        targets: xpText,
        y: height * 0.40,
        alpha: 0,
        duration: 1400,
        ease: 'Power2',
        onComplete: () => xpText.destroy(),
      });
    }
  }
}

RendererRegistry.register('football', FootballScene, 'phaser_2d');
